import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/helpers/apiClient';
import { EVENTOS, setUserProperties, trackEvent } from '@/helpers/analytics';
import { authClient } from '@/helpers/authClient';

import { ErrorHandler } from '@/utils/ErrorHandler';

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null);
  const empresa = ref(null);
  const loading = ref(false);

  // Computed state for better-auth session
  const sessionData = authClient.useSession();

  // Getters
  const isAuthenticated = computed(() => !!user.value);
  const isSuperAdmin = computed(() => user.value?.rol === 'superadmin');
  const isOwner = computed(() => ['dueño'].includes(user.value?.rol));
  const isAdmin = computed(() => ['dueño', 'administrador'].includes(user.value?.rol));
  const nombreCompleto = computed(() => 
    user.value ? `${user.value.nombre} ${user.value.apellido || ''}`.trim() : ''
  );
  
  // Check if Google is linked
  const isGoogleLinked = computed(() => {
    return user.value?.accounts?.some(acc => acc.providerId === 'google') || false;
  });

  async function login(email, password, remember = false) {
    loading.value = true;
    try {
      const { error } = await authClient.signIn.email({ 
        email, 
        password,
        rememberMe: remember
      });
      
      if (error) {
        return { success: false, error: ErrorHandler.getMessage(error) };
      }

      await fetchProfile();

      localStorage.setItem('onboarding_completed', 'true');
      sessionStorage.setItem('onboarding_completed', 'true');

      // `login` es un evento reservado de GA4: usarlo con ese nombre exacto
      // alimenta los reportes de retención que Google trae hechos.
      trackEvent(EVENTOS.LOGIN, { method: 'email' });

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: ErrorHandler.getMessage(error, 'Error al iniciar sesión'),
      };
    } finally {
      loading.value = false;
    }
  }

  async function register(data) {
    loading.value = true;
    try {
      const { error } = await authClient.signUp.email({
        email: data.email,
        password: data.password,
        name: data.nombre,
      });

      if (error) {
        return { success: false, error: ErrorHandler.getMessage(error) };
      }

      await fetchProfile();

      // `sign_up` es evento reservado de GA4: con ese nombre alimenta el
      // reporte de adquisición sin configurar nada extra.
      trackEvent(EVENTOS.SIGN_UP, { method: 'email' });

      sessionStorage.setItem('show_onboarding', 'true');
      localStorage.removeItem('onboarding_completed');
      sessionStorage.removeItem('onboarding_completed');

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.getMessage(error, 'Error al registrar'),
      };
    } finally {
      loading.value = false;
    }
  }

  async function loginWithGoogle() {
    loading.value = true;
    try {
      const { error } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: '/dashboard'
      });
      if (error) {
        return { success: false, error: ErrorHandler.getMessage(error) };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorHandler.getMessage(error, 'Error al iniciar sesión con Google') };
    } finally {
      loading.value = false;
    }
  }

  async function linkGoogle() {
    loading.value = true;
    try {
      const { error } = await authClient.linkSocial({ provider: 'google', callbackURL: '/sistema/perfil' });
      if (error) {
        return { success: false, error: ErrorHandler.getMessage(error) };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: ErrorHandler.getMessage(error, 'Error al iniciar vinculación') };
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    // Antes del signOut: después el usuario ya no está y no se puede saber
    // con qué plan ni rol se fue.
    trackEvent(EVENTOS.LOGOUT);
    try {
      await authClient.signOut();
    } catch {
      // Ignorar errores
    } finally {
      user.value = null;
      empresa.value = null;
      
      localStorage.removeItem('auth');
      sessionStorage.removeItem('auth');
      
      localStorage.removeItem('onboarding_completed');
      sessionStorage.removeItem('onboarding_completed');
      sessionStorage.removeItem('show_onboarding');
    }
  }

  async function fetchProfile() {
    try {
      const response = await api.get('/api/usuarios/me');
      if (response.data.user) {
        user.value = response.data.user;
        empresa.value = response.data.user.empresa;
        // Keep a copy in localStorage for immediate sync across tabs if needed
        localStorage.setItem('auth', JSON.stringify({ user: response.data.user }));

        // Propiedades de cuenta, no de persona: permiten cortar cualquier
        // métrica por plan o por rol sin identificar a nadie.
        setUserProperties({
          plan: response.data.user.empresa?.plan,
          rol: response.data.user.rol,
          empresaId: response.data.user.empresa_id,
        });

        return true;
      }
    } catch (error) {
      console.error('Error fetching profile', error);
      if (error.response?.status === 401) {
        logout();
      }
    }
    return false;
  }

  async function updateProfile(data) {
    loading.value = true;
    try {
      const response = await api.put('/api/usuarios/me', data);
      user.value = { ...user.value, ...response.data.data };
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.getMessage(error, 'Error al actualizar perfil'),
      };
    } finally {
      loading.value = false;
    }
  }

  async function changePassword(currentPassword, newPassword) {
    loading.value = true;
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      });
      if (error) return { success: false, error: ErrorHandler.getMessage(error) };
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: ErrorHandler.getMessage(error, 'Error al cambiar contraseña'),
      };
    } finally {
      loading.value = false;
    }
  }

  // Verificar si tiene un permiso específico
  function hasPermission(permission) {
    if (!user.value) return false;
    if (user.value.rol === 'superadmin') return true; // Superadmin tiene todos los permisos
    if (user.value.rol === 'dueño') return true;

    // Permisos según rol
    const rolePermissions = {
      administrador: [
        'usuarios:view', 'usuarios:create', 'usuarios:edit',
        'productos:view', 'productos:create', 'productos:edit', 'productos:delete',
        'ventas:view', 'ventas:create', 'ventas:edit', 'ventas:delete',
        'presupuestos:view', 'presupuestos:create', 'presupuestos:edit', 'presupuestos:delete',
        'clientes:view', 'clientes:create', 'clientes:edit', 'clientes:delete',
        'gastos:view', 'gastos:create', 'gastos:edit', 'gastos:delete',
        'reportes:view', 'stock:view', 'stock:edit',
      ],
      vendedor: [
        'productos:view',
        'ventas:view', 'ventas:create',
        'presupuestos:view', 'presupuestos:create', 'presupuestos:edit',
        'clientes:view', 'clientes:create',
        'stock:view',
      ],
      viewer: [
        'productos:view', 'ventas:view', 'presupuestos:view',
        'clientes:view', 'reportes:view', 'stock:view',
      ],
    };

    const permisos = rolePermissions[user.value.rol] || [];
    return permisos.includes(permission);
  }

  // Inicializar usuario desde localStorage o sessionStorage si existe
  function init() {
    let authData = localStorage.getItem('auth') || sessionStorage.getItem('auth');
    
    if (authData) {
      try {
        const parsed = JSON.parse(authData);
        if (parsed.user) {
          user.value = parsed.user;
          empresa.value = parsed.user.empresa;
        }
      } catch (error) {
        console.error('Error parsing auth data:', error);
      }
    }
    
    // Si tenemos sesión, buscar perfil para tener los datos más frescos (empresa, accounts)
    if (user.value) {
      fetchProfile();
    }
  }

  // Inicializar al crear el store
  init();

  return {
    // State
    user,
    empresa,
    loading,
    sessionData,
    // Getters
    isAuthenticated,
    isSuperAdmin,
    isOwner,
    isAdmin,
    nombreCompleto,
    isGoogleLinked,
    // Actions
    login,
    register,
    loginWithGoogle,
    linkGoogle,
    logout,
    fetchProfile,
    updateProfile,
    changePassword,
    hasPermission,
    init,
  };
});
